<!--
  @component GuardIO — client-side RBAC pre-check lens (UX only).
  chain:  useSecurityIO().can(op) → this → ctx.guard
  shape:  deny-by-default RBAC PRE-CHECK via the control-plane permissions
          lens — disable/hide actions the principal's role can't perform.
          The deployment server's floor is the SOLE authority (it re-checks
          every op deny-by-default). This is UX, never enforcement.
-->
<script lang="ts" module>
  import { getContext } from "svelte";
  import { GUARD_IO_KEY } from "./security-io.js";
  import type { GuardIOContext } from "./security-io.js";

  export { GUARD_IO_KEY };
  export type { GuardIOContext };

  export function useGuardIO(): GuardIOContext {
    const ctx = getContext<GuardIOContext | undefined>(GUARD_IO_KEY);
    if (!ctx) throw new Error("useGuardIO() called outside of a GuardIO tree.");
    return ctx;
  }
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useSecurityIO } from "./SecurityIO.svelte";
  import type { OpClassification } from "./security-io.js";

  interface Props { children?: Snippet; }
  const { children }: Props = $props();
  const parent = useSecurityIO();

  const ctx: GuardIOContext = {
    get role() { return parent.role; },
    can(op) { return parent.can(op); },
    denyReason(op) {
      if (parent.can(op)) return null;
      const { resourceType, action } = parent.classify(op);
      return `role "${parent.role}" cannot ${action} ${resourceType}`;
    },
    classify(op): OpClassification { return parent.classify(op); },
  } satisfies GuardIOContext;

  setContext(GUARD_IO_KEY, ctx);
</script>

{@render children?.()}
